import { useMemo } from 'react';
import { AvailableExtra, Currency } from '~/lib/types/basket-types';
import { P2GCheckBox, P2GGrid, P2GText } from '@parcel2go/core-ui';
import { UpsellDefaultContent, UpsellDefaultTitle } from './upsell-default-content';
import { toDisplayCurrency } from '~/lib/prices';
import { Trans, useTranslation } from 'react-i18next';
import { useBasketDeliveryGuaranteeDate } from '~/hooks/use-delivery-guarantee-date';
import Price from '~/components/core/price/price';
import { TranslationNamespaces } from '~/lib/constants';
import { useRawCmsContent } from '~/hooks/use-raw-cms-content';
import { useTaxName } from '@parcel2go/npm-p2g-app';

type UpsellProps = {
  extra: AvailableExtra;
  currency: Currency;
  selected: boolean;
  quoteIsDropOff: boolean;
  onChange: (extra: string, selected: boolean) => void;
  loading: boolean;
};

export default function Upsell({ extra, currency, selected, onChange, loading, quoteIsDropOff }: UpsellProps) {
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const price = useMemo(() => toDisplayCurrency(currency, extra.price.net), [extra, currency]);
  const { deliveryGuaranteeDate, maxDays, cutOffTime, cutOffDate } = useBasketDeliveryGuaranteeDate();
  const taxName = useTaxName();
  const title = useRawCmsContent(`upsell-${extra.key}-title`);
  const content = useRawCmsContent(`upsell-${extra.key}-content`);

  return (
    <>
      <P2GGrid
        container
        direction="column"
        gap={1}
        onClick={() => onChange(extra.key, !selected)}
        dataTestId={`extra-${extra.key}-container`}
      >
        <P2GGrid item>
          <P2GGrid container wrap="nowrap" gap={2}>
            <P2GGrid item>
              <P2GCheckBox
                dataTestId={`extra-${extra.key}-checkbox`}
                checked={selected}
                inputProps={{
                  checked: selected
                }}
                disabled={loading}
                error={false}
                labelPlacement="end"
                label={null}
                size="small"
                variant="primary"
              />
            </P2GGrid>
            <P2GGrid item flexGrow={1}>
              <P2GText variant="subtitle1" fontWeight="bold" component="div" dataTestId={`extra-${extra.key}-name`}>
                {t(`UPSELL_LIST_${extra.key}_TITLE`.toUpperCase(), {
                  extra: extra,
                  deliveryGuaranteeDate: deliveryGuaranteeDate?.toLocaleDateString(undefined, {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric'
                  }),
                  price: price,
                  defaultValue: title || UpsellDefaultTitle(extra.key),
                  taxName: taxName
                })}
              </P2GText>
            </P2GGrid>
            <P2GGrid item>
              <Price
                variant="subtitle1"
                incPlus={true}
                incAsterisk={extra.price.tax > 0}
                fontWeight="bold"
                currency={currency}
                dataTestId={`extra-${extra.key}-price`}
                value={extra.price.net}
              />
            </P2GGrid>
          </P2GGrid>
        </P2GGrid>
        <P2GGrid item>
          <P2GText variant="body2">
            <Trans
              i18nKey={`UPSELL_LIST_${extra.key}_CONTENT`.toUpperCase()}
              t={t}
              defaults={content || UpsellDefaultContent(extra.key, quoteIsDropOff)}
              values={{
                extra: extra,
                deliveryGuaranteeDate: deliveryGuaranteeDate?.toLocaleDateString(undefined, {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric'
                }),
                maxDays: maxDays?.toString(),
                cutOffTime: cutOffTime,
                cutOffDate: cutOffDate,
                price: price,
                taxName: taxName
              }}
              components={[<P2GText colour="primary" variant="caption" component="p" pt={1} />, <strong />]}
            />
          </P2GText>
        </P2GGrid>
      </P2GGrid>
    </>
  );
}
